/**
 * ROUTER-LEVEL HEAD RE-ASSERT (C-357 clause 4 — the COVERAGE half of the
 * head-authority contract).
 *
 * WHAT PROBLEM THIS CLOSES. C-356 proved there is exactly ONE WRITER of the
 * managed head fields (`.dcs/seo.yaml`). It did NOT prove every route re-asserts
 * it. A view that writes no head at all is "silent": on a hard navigation it is
 * CORRECT (the baked `<head>` of the fetched document is the approved one and
 * nothing overwrites it), but on the first in-app click it leaves the PREVIOUS
 * route's title/description/canonical in the DOM. Measured on boogie-babies:
 * `/pricing` -> `/parties` still claims `canonical=/pricing`. The audit reports
 * this as `uncovered-route` and deliberately does not fail on it, because the
 * fix is one router hook in the platform, not eleven views remembering.
 *
 * WHY THIS FILE IS FULL OF REFUSALS. Turning silence from "safe" into "asserts
 * whatever the lookup returns" is a LOADED CHANGE: on every route whose baked
 * head is already correct, a wrong lookup is a live regression on a paying
 * customer. A fleet sweep on 2026-07-28 measured three ways the obvious
 * implementation breaks, and each one is encoded here as a hard rule.
 *
 *   R1  DRIVE THE LOOKUP FROM `.dcs/pages.yaml`, NEVER FROM `to.meta.pageSlug`.
 *       KEPT carries `meta.pageSlug` on every route, which makes the meta-driven
 *       version look like the obvious choice. It is a trap: KEPT's two DYNAMIC
 *       routes carry the PLACEHOLDER keys `blog-post` and `topic`, and neither
 *       key exists in its `seo.yaml`. A meta-driven build therefore collapses
 *       21 blog posts + 3 topic pages to `global.defaultTitle` ON THE TOP PAYER.
 *       The pages.yaml path->slug map resolves all 24 correctly, because it is
 *       the SAME map the build-time emitter walked to bake them.
 *       There is no read of `meta` anywhere in this file. That is the fix.
 *
 *   R2  NO MATCH => TOUCH NOTHING. Not `global.defaultTitle`, not a clear, not
 *       a "best effort" — nothing. boogie-babies' catch-all is
 *       `/:pathMatch(.*)*` and its runtime path is the URL the visitor
 *       MISTYPED (`/nope-xyz`), never the pages.yaml path `/404`; that repo
 *       carries no `meta.pageSlug` at all. So a no-match is the NORMAL case
 *       there, and any fallback write would clobber a correct baked head on
 *       every 404 the site serves. No `seo.yaml` entry can fix it — the lookup
 *       KEY is the wrong thing, not the value.
 *
 *   R3  NORMALIZE TRAILING SLASHES ON BOTH SIDES. KEPT declares `/blog/` in
 *       pages.yaml and `/blog` in router.ts. Naive path equality misses it and
 *       the blog index silently joins the no-match set. The root path `/` must
 *       NOT normalize to the empty string. A manifest that declares BOTH forms
 *       is rejected at BUILD time (see `findDuplicateNormalizedPaths`), because
 *       the emitter's last-write wins the output file while a lookup map's
 *       first-write wins the runtime — a duplicate therefore guarantees
 *       divergence whichever side you pick, so neither side may pick.
 *
 *   R4  A FAILED NAVIGATION IS NOT A NAVIGATION. Vue Router calls
 *       `afterEach(to, from, failure)` for ABORTED, CANCELLED and DUPLICATED
 *       navigations, and when it fails the visible route stays `from`. A hook
 *       that reads only `to` therefore asserts the head of a page the visitor is
 *       NOT on: an auth guard rejecting `/pricing -> /account` leaves them on
 *       pricing under the ACCOUNT head, `noindex` included. Verified at source
 *       on vue-router 4.6.3 (`triggerAfterEach(toLocation, from, failure)` runs
 *       unconditionally; `finalizeNavigation` — the only assignment to
 *       `currentRoute` — is skipped when a failure is present) and empirically:
 *       a `beforeEach` returning `false` produced `afterEach(to=/account,
 *       from=/pricing, failure.type=4)` with `currentRoute` still `/pricing`.
 *       So: `failure` truthy => write NOTHING. This rule exists because the
 *       obvious version of THIS FIX invents its own divergence class.
 *
 *   R5  THE INITIAL DOCUMENT IS NOT RE-ASSERTED. Its head is the baked,
 *       owner-approved one and is correct exactly once — on that document.
 *       Re-asserting buys nothing and puts the one head that is already right on
 *       the same code path as the one that can be wrong. This is ENFORCED, not
 *       assumed: `afterEach` alone does not guarantee it, because the generated
 *       app entry installs the router BEFORE awaiting `router.isReady()`
 *       (`packages/create-dcs-site/templates/vue-spa/src/main.ts`), so a hook
 *       registered on the adoption line can and does observe the initial
 *       navigation. The guard recognises vue-router's START location
 *       structurally (`name` unset, `matched` empty, path `/`) and skips at most
 *       ONE write, ever.
 *
 *   S6  THE BAKED MANAGED TAGS ARE SWEPT ONCE, ON THE FIRST MATCH. See DEFECT
 *       F-A immediately below and `sweepBakedHeadTags.ts` for the four refusals
 *       that make the sweep safe (JSON-LD and `<title>` unreachable; never
 *       sweep a tag the runtime is about to declare; snapshot at install time).
 *
 * MEASURED DEFECT F-A — **CLOSED 2026-08-11 by SWEEP ONCE** (Q-453 = A, C-603).
 * The production-shaped fixture (`installSeoHead.integration.test.ts`) put a
 * REAL Unhead client (both 1.11.20 and 2.0.19) on a REAL baked document and
 * counted `document.head`. Two facts came out of it:
 *
 *   GOOD — Unhead RECONCILES against the baked static head. It does not add
 *   alongside it. `<title>`, description, canonical, robots, og:* and twitter:*
 *   all stayed at EXACTLY ONE element through six navigations, with the new
 *   route's value. The feared "two titles, two canonicals" outcome does not
 *   happen on either version, and is in fact unreachable: Unhead dedupes by tag
 *   identity even across separate entries.
 *
 *   BAD — Unhead only REMOVES side effects it OWNS, and it never owned the
 *   baked tags. So a managed tag that is present in the BAKED head and ABSENT
 *   from the runtime entry is left stranded in the DOM. Concretely: land on
 *   baked `/blog/post-a` (which bakes `<meta name="keywords">`), click through
 *   to `/blog/post-b` (whose seo.yaml declares none) and post A's keywords are
 *   still there while title, description and canonical have all moved. This is
 *   C-361 F4 — the divergence `includeKeywords: true` was added to close —
 *   surviving in its baked->runtime half. The class is EVERY OPTIONAL managed
 *   tag, not just keywords: the fixture reproduces it for `link[rel=alternate]`
 *   hreflang too, and `robots`/`article:*` are exposed the same way.
 *
 *   It is invisible to the 610 fake-driven tests by construction: when the tag
 *   was written by a PREVIOUS re-assert, Unhead owns it and drops it correctly,
 *   which is all a recording fake can ever model.
 *
 *   THE FIX, DECIDED BY THE OWNER RATHER THAN INVENTED HERE. Q-453 chose A —
 *   SWEEP ONCE — over "always emit" (which structurally cannot fix hreflang or
 *   `article:*`, and forks the shared `buildHeadTags` path) and "stop baking
 *   optional tags" (the only crawler-VISIBLE option, and it changes them by
 *   deletion). On the first MATCHED in-app navigation this module now removes
 *   the baked managed tags the incoming input does not re-declare, so the
 *   runtime owns the managed head from then on. The sweep lives in
 *   `sweepBakedHeadTags.ts` and reuses the EMITTER'S OWN managed-set predicates
 *   (`isManagedMetaTag` / `isManagedLinkRel` in `spliceHeadHtml.ts`) rather
 *   than a second list, because the named residual of option A was those two
 *   lists drifting apart.
 *
 *   Crawler-visible bytes are UNCHANGED: the sweep is reachable only from a
 *   matched in-app navigation, and R5 already refuses to write for the fetched
 *   document. The fixture's `DEFECT F-A` assertions (ITEM 2a / 2d, plus the
 *   third instance in ITEM 8a) are flipped to the swept expectation, which was
 *   the acceptance test this docstring named.
 *
 * WHAT IT RE-ASSERTS, AND WHAT IT DELIBERATELY DOES NOT.
 *   - `title`, `meta`, `link` — the managed fields P1 (`headHonesty.ts`) grades
 *     and the ones that go stale on SPA navigation. `meta[name=keywords]` is
 *     among them: the emitter passes `includeKeywords: true`, so a re-assert
 *     that omitted it would leave post A's baked keywords in the DOM on
 *     post A -> post B while every other managed field moved (C-361 F4).
 *   - NOT `script` (JSON-LD). The baked graph is built with inputs that do not
 *     exist at runtime (`content.yaml` reviews, business license, FAQ pairs,
 *     breadcrumb trails). Re-asserting a POORER graph would not correct the
 *     baked one, it would add a second, conflicting set of nodes — a new
 *     divergence class invented by the fix. Leaving it alone is the same
 *     judgement as R2: do not overwrite what you cannot faithfully reproduce.
 *     Named residual: after an in-app navigation the JSON-LD still describes the
 *     first document. Crawlers read the fetched document, where it is correct.
 *
 * IT RESOLVES THROUGH THE SHIPPED EMITTER (`buildHeadTags`) AND CARRIES THE TWO
 * BUILD-TIME-ONLY INPUTS. A second head-building code path would be its own
 * divergence source, so this calls the exact function the build calls. Two
 * inputs the plain runtime composable does NOT carry are threaded through here
 * because C-357 measured both as live divergences:
 *   - `fallbackTitle` (the pages.yaml route title). The emitter resolves
 *     `page.title || <route title>`; the runtime had no fallback and collapsed
 *     to `global.defaultTitle`. mi-handyman had FOUR such routes.
 *   - the `noindex` force. `dcsSeoPlugin({ noindex: [...] })` is a BUILD-TIME
 *     force; the runtime `__DCS_SEO__` global is the raw seo.yaml, so a re-assert
 *     without it republishes `index, follow` over a baked `noindex` — measured
 *     live on iron-oak `/account` and `/projects`. Re-asserting without carrying
 *     the list would ship that known regression by construction.
 *
 * NO vue-router IMPORT. The router is accepted structurally (anything with an
 * `afterEach`), so this package gains no dependency and the tests need no router
 * instance to prove the rules.
 */

import type { SeoConfiguration } from '../types/seo'
import { buildHeadTags, type HeadMetaTag, type HeadLinkTag } from './headTags'
import {
  snapshotBakedManagedHeadTags,
  sweepBakedManagedHeadTags,
} from './sweepBakedHeadTags'

// =============================================================================
// Structural types — no vue-router, no unhead import
// =============================================================================

/**
 * What this module reads off a resolved route.
 *
 * `path` is the ONLY field consulted for the lookup. `name` and `matched` are
 * read off `from` ALONE, and only to recognise vue-router's START location for
 * R5; nothing about the head is ever derived from them.
 */
export interface SeoHeadRouteLike {
  path: string
  /** `from` only, R5: START location has no name. Never used for the lookup. */
  name?: unknown
  /** `from` only, R5: START location matched nothing. Never used for the lookup. */
  matched?: readonly unknown[]
}

/**
 * The slice of a vue-router instance this needs.
 *
 * The hook takes the FULL vue-router `afterEach` arity. `failure` is not
 * optional decoration — see R4: dropping it is what makes an aborted navigation
 * assert the head of a page nobody is on.
 *
 * NOTE THE ABSENCE: there is no `meta` on {@link SeoHeadRouteLike}, and that
 * absence is PINNED at compile time by {@link SEO_HEAD_ROUTE_HAS_NO_META}
 * below rather than left as friction.
 */
export interface SeoHeadRouterLike {
  afterEach(
    hook: (to: SeoHeadRouteLike, from?: SeoHeadRouteLike, failure?: unknown) => void
  ): unknown
}

// =============================================================================
// The compile-time half of R1 (C-361 F7)
// =============================================================================

/**
 * COMPILE-TIME PIN: the route type this module accepts must carry NO `meta`.
 *
 * WHY A PIN AND NOT JUST AN ABSENCE. The absent property was described as
 * "friction, not a gate", and the cross-model review proved it: a future edit
 * adding `meta.pageSlug` as a NO-MATCH FALLBACK keeps every existing test green,
 * because the matched-route tests supply POISONED meta (so a fallback never
 * fires there) and the no-match tests supply NONE (so a fallback finds nothing).
 * Widening the interface is the mechanical prerequisite for reading `meta`, so
 * this is where R1 gets a real gate.
 *
 * Modelled on `APPLY_HEAD_TAKES_NO_ARGUMENTS` in `headContract.ts`, and it lives
 * in a CHECKED SOURCE FILE for the same reason: `packages/cms/tsconfig.json`
 * excludes `**\/*.test.ts`, so a `@ts-expect-error` in the test file would be a
 * comment wearing a gate's clothes. Add `meta` to {@link SeoHeadRouteLike} and
 * the assignment below stops compiling — red in this package's `type-check`,
 * and through the published `.d.ts` in every site that runs one.
 *
 * HONEST LIMIT, stated rather than implied: this pins the TYPE SURFACE. It
 * cannot stop `(to as any).meta`, exactly as `APPLY_HEAD_TAKES_NO_ARGUMENTS`
 * cannot stop an `as any` argument. The behavioural half is the no-match test
 * that navigates with a poisoned meta pointing at a REAL seo.yaml slug — the
 * case the review found missing.
 */
export type SeoHeadRouteHasNoMeta = 'meta' extends keyof SeoHeadRouteLike ? false : true

export const SEO_HEAD_ROUTE_HAS_NO_META: SeoHeadRouteHasNoMeta = true

/** A live unhead entry (`head.push(...)` result) — only `patch` is used. */
export interface SeoHeadEntryLike {
  patch(input: SeoHeadInput): void
}

/** The unhead client (`createHead()`), passed in explicitly — see below. */
export interface SeoHeadClientLike {
  push(input: SeoHeadInput, options?: Record<string, unknown>): SeoHeadEntryLike
}

/** The head payload handed to unhead. Managed fields only. */
export interface SeoHeadInput {
  title: string
  meta: HeadMetaTag[]
  link: HeadLinkTag[]
}

/** One route from `.dcs/pages.yaml`, as the emitter reads it. */
export interface SeoHeadPageRoute {
  slug: string
  path: string
  title?: string
}

/**
 * The runtime manifest injected by `dcsSeoPlugin` as `__DCS_PAGES__`.
 *
 * It carries the `noindex` list alongside the routes because that list is a
 * plugin OPTION, not a file — without it here the runtime cannot know that a
 * route was baked `noindex, nofollow`.
 */
export interface SeoHeadPagesManifest {
  routes: SeoHeadPageRoute[]
  noindex?: string[]
}

/** Why a navigation did or did not result in a head write. */
export type SeoHeadResolutionReason =
  /** Matched a manifest route; the head was re-asserted. */
  | 'applied'
  /** No manifest reached the runtime — the module is inert (never a fallback). */
  | 'no-manifest'
  /** The path is not a manifest route — R2: the baked head is left alone. */
  | 'no-match'
  /**
   * R4 — the navigation FAILED (aborted / cancelled / duplicated), so the
   * visitor is still on `from` and `to` is a page they never reached. Reported
   * rather than silent: "the guard rejected it" and "the hook never ran" must
   * not look the same from the outside.
   */
  | 'navigation-failed'
  /**
   * R5 — the initial document. Its baked head is the approved one; the hook
   * observed the navigation (installation raced `router.isReady()`) and
   * deliberately declined to write.
   */
  | 'initial-navigation'

export interface SeoHeadResolution {
  /** The path as the router reported it. */
  path: string
  /** The path after R3 normalization — what the lookup actually used. */
  normalizedPath: string
  matched: SeoHeadPageRoute | null
  reason: SeoHeadResolutionReason
  /** The resolved `<title>`, or `null` when nothing was written. */
  title: string | null
  /**
   * How many BAKED managed tags this navigation swept out of the DOM (S6).
   *
   * Non-zero on AT MOST ONE navigation per session — the first match, which
   * spends the sweep latch — and `0` on every other, including every
   * no-match / failed / initial resolution. Reported for the same reason
   * `no-match` is: "it swept nothing" and "it never swept" must not look the
   * same from the outside.
   */
  sweptBakedTags: number
}

export interface InstallSeoHeadOptions {
  /**
   * The unhead client the app already created (`const head = createHead()`).
   *
   * REQUIRED, and it cannot be discovered instead. `injectHead()` calls Vue's
   * `inject`, which throws outside a component `setup()` — and a router
   * `afterEach` hook is exactly that. Passing it is the honest API; guessing at
   * a global would fail at runtime on the first navigation.
   */
  head: SeoHeadClientLike
  /** Route manifest. Defaults to the injected `__DCS_PAGES__.routes`. */
  pages?: SeoHeadPageRoute[]
  /** SEO config. Defaults to the injected `__DCS_SEO__`. */
  seo?: SeoConfiguration
  /** Build-time noindex list. Defaults to the injected `__DCS_PAGES__.noindex`. */
  noindex?: string[]
  /**
   * Observability hook, called once per navigation with the resolution —
   * INCLUDING the no-match case. Exists so "it did nothing" is distinguishable
   * from "it never ran", which is the vacuous-green shape this estate keeps
   * counting. Never affects behaviour.
   */
  onResolve?: (resolution: SeoHeadResolution) => void
}

// =============================================================================
// Injected globals (mirrors `useSEO`'s `__DCS_SEO__` handling)
// =============================================================================

declare const __DCS_SEO__: SeoConfiguration | undefined
declare const __DCS_PAGES__: SeoHeadPagesManifest | undefined

function readInjectedSeo(): SeoConfiguration | undefined {
  try {
    if (typeof __DCS_SEO__ !== 'undefined' && __DCS_SEO__ !== null) return __DCS_SEO__
  } catch {
    /* not defined — fine */
  }
  return undefined
}

function readInjectedPages(): SeoHeadPagesManifest | undefined {
  try {
    if (typeof __DCS_PAGES__ !== 'undefined' && __DCS_PAGES__ !== null) return __DCS_PAGES__
  } catch {
    /* not defined — fine */
  }
  return undefined
}

// =============================================================================
// R3 — path normalization
// =============================================================================

/**
 * Normalize a path for comparison. Applied to BOTH sides — the manifest path and
 * the router path — because normalizing only one side is how `/blog/` and
 * `/blog` stayed unequal.
 *
 * - drops `?query` and `#hash` (a router path should not carry them; a caller's
 *   `fullPath` might, and silently no-matching on a query string would be a
 *   surprise no one would ever debug)
 * - strips trailing slashes
 * - PRESERVES the root: `/` and `''` both normalize to `/`, never to `''`.
 *   Collapsing root to the empty string makes the home route unmatchable, which
 *   is the highest-traffic route on every site in the fleet.
 */
export function normalizeSeoHeadPath(input: string): string {
  if (typeof input !== 'string') return '/'
  let p = input
  const hash = p.indexOf('#')
  if (hash !== -1) p = p.slice(0, hash)
  const query = p.indexOf('?')
  if (query !== -1) p = p.slice(0, query)
  p = p.replace(/\/+$/, '')
  return p === '' ? '/' : p
}

/**
 * Find manifest paths that COLLIDE after R3 normalization.
 *
 * WHY THIS IS A BUILD ERROR AND NOT A TIEBREAK (C-361 F6, verified). A manifest
 * carrying both `/blog` and `/blog/` resolves OPPOSITE ways on the two sides of
 * the contract:
 *
 *   - the EMITTER walks the manifest in order and writes one file per route, so
 *     for colliding paths the LAST write is the one left on disk;
 *   - a lookup Map keyed on the normalized path takes the FIRST entry, because
 *     that is what "don't clobber" means for a map.
 *
 * Whichever side is "fixed", the other is then wrong — the duplicate does not
 * merely risk divergence, it GUARANTEES it. There is no correct pick, so the
 * build must refuse instead of picking. The two entries have different slugs and
 * therefore different seo.yaml identities; only the site owner knows which page
 * that URL is.
 *
 * @returns the normalized paths that appear more than once, each with the
 *   colliding raw entries, in first-seen order. Empty array = clean.
 */
export function findDuplicateNormalizedPaths(
  routes: readonly SeoHeadPageRoute[]
): Array<{ normalizedPath: string; routes: SeoHeadPageRoute[] }> {
  const groups = new Map<string, SeoHeadPageRoute[]>()
  for (const route of routes) {
    if (!route || typeof route.path !== 'string' || route.path === '') continue
    const key = normalizeSeoHeadPath(route.path)
    const bucket = groups.get(key)
    if (bucket) bucket.push(route)
    else groups.set(key, [route])
  }
  const dupes: Array<{ normalizedPath: string; routes: SeoHeadPageRoute[] }> = []
  for (const [normalizedPath, group] of groups) {
    if (group.length > 1) dupes.push({ normalizedPath, routes: group })
  }
  return dupes
}

/** Human-readable duplicate report, shared by the build throw and the runtime log. */
export function formatDuplicateNormalizedPaths(
  dupes: ReadonlyArray<{ normalizedPath: string; routes: SeoHeadPageRoute[] }>
): string {
  const lines = dupes.map(
    (d) =>
      `  ${d.normalizedPath} <= ` +
      d.routes.map((r) => `${JSON.stringify(r.path)} (slug ${JSON.stringify(r.slug)})`).join(', ')
  )
  return (
    `pages.yaml declares ${dupes.length} route path(s) that collide after trailing-slash ` +
    `normalization:\n${lines.join('\n')}\n` +
    `The build-time emitter's LAST write wins the output file while the runtime lookup's ` +
    `FIRST entry wins the head — so a colliding manifest guarantees a baked/runtime ` +
    `divergence whichever side is preferred. There is no safe default: the entries carry ` +
    `different slugs and therefore different .dcs/seo.yaml identities. Delete or rename one.`
  )
}

/**
 * Build the normalized path -> route lookup.
 *
 * On a duplicate normalized path the FIRST entry wins. That is a LAST-RESORT
 * DETERMINISM RULE, not the contract: duplicates are rejected at build time by
 * `findDuplicateNormalizedPaths` (R3), and this branch only ever runs for a
 * manifest that reached a browser without passing that gate. It is deliberately
 * not "clever" — an arbitrary-but-stable pick is the least bad thing to do once
 * the loud failure has already been missed.
 */
export function buildSeoHeadRouteMap(
  routes: readonly SeoHeadPageRoute[]
): Map<string, SeoHeadPageRoute> {
  const map = new Map<string, SeoHeadPageRoute>()
  for (const route of routes) {
    if (!route || typeof route.path !== 'string' || route.path === '') continue
    const key = normalizeSeoHeadPath(route.path)
    if (!map.has(key)) map.set(key, route)
  }
  return map
}

// =============================================================================
// R5 — recognising the initial navigation
// =============================================================================

/**
 * Is this `from` route vue-router's START location?
 *
 * `START_LOCATION_NORMALIZED` (vue-router 4.6.3, verified at source) is
 * `{ path: '/', name: undefined, params: {}, query: {}, hash: '', fullPath: '/',
 * matched: [], meta: {}, redirectedFrom: undefined }`. The discriminating
 * triple is `name` unset + `matched` empty + path `/`: any route the visitor has
 * actually been on has matched at least one record.
 *
 * Structural on purpose — importing vue-router to compare against the exported
 * `START_LOCATION` would give this package a framework dependency for one
 * identity check, and would still be a reference comparison that a re-exported
 * or wrapped router could defeat.
 */
function isStartLocation(from: SeoHeadRouteLike | undefined): boolean {
  if (!from || typeof from !== 'object') return false
  if (from.name !== undefined && from.name !== null) return false
  if (!Array.isArray(from.matched) || from.matched.length > 0) return false
  return normalizeSeoHeadPath(typeof from.path === 'string' ? from.path : '') === '/'
}

// =============================================================================
// The install
// =============================================================================

/**
 * Re-assert the `.dcs/seo.yaml` head on every in-app navigation.
 *
 * Adoption is one line at app entry, after the head client exists:
 *
 * ```ts
 * const head = createHead()
 * app.use(head)
 * installSeoHead(router, { head })
 * ```
 *
 * DOES NOT WRITE FOR THE INITIAL DOCUMENT (R5) — and that is now IMPLEMENTED
 * rather than asserted. The previous version of this docstring claimed
 * `afterEach` "is registered for SUBSEQUENT navigations only"; there was no such
 * guard, and the claim is false whenever the hook is installed while the initial
 * navigation is still in flight — which is precisely what the adoption snippet
 * above does in the generated app entry, where `app.use(router)` runs BEFORE
 * `router.isReady()` is awaited. The head of the document the browser actually
 * fetched is the baked, owner-approved one and is correct exactly once, on that
 * document; re-asserting it buys nothing and puts the one head that is already
 * right on the same code path as the one that can be wrong. Installing AFTER
 * `await router.isReady()` reaches the same outcome by a different route (the
 * hook simply never sees that navigation) and is equally supported — the guard
 * exists so the outcome does not depend on remembering which.
 *
 * NAMED RESIDUAL (R5, dev only): under `vite dev` there is no baked per-route
 * `<head>` — the shell's is generic — so the skipped initial write leaves the
 * generic head in place until the first in-app navigation. Production is
 * unaffected because production HAS a baked head. The skip is reported through
 * {@link InstallSeoHeadOptions.onResolve} as `initial-navigation`, so it is
 * observable rather than mysterious.
 *
 * @returns the hook's own removal function, as vue-router's `afterEach` returns.
 */
export function installSeoHead(
  router: SeoHeadRouterLike,
  options: InstallSeoHeadOptions
): unknown {
  const head = options.head
  const manifest = readInjectedPages()
  const routes = options.pages ?? manifest?.routes
  const seo = options.seo ?? readInjectedSeo()
  const noindex = options.noindex ?? manifest?.noindex ?? []
  const onResolve = options.onResolve

  const noindexSet = new Set(noindex)
  const routeMap =
    routes && routes.length > 0 ? buildSeoHeadRouteMap(routes) : null

  // R3 — a colliding manifest is a BUILD error (`dcsSeoPlugin` throws on it).
  // Reaching a browser means that gate was bypassed: say so once, loudly, and
  // carry on with the deterministic pick. This deliberately does NOT throw — by
  // the time this line runs a real visitor is looking at the page, and the worst
  // outcome of a refusal must be that they keep the owner-approved head.
  if (routes && routes.length > 0) {
    const dupes = findDuplicateNormalizedPaths(routes)
    if (dupes.length > 0) {
      console.error(`[dcs-seo] installSeoHead: ${formatDuplicateNormalizedPaths(dupes)}`)
    }
  }

  // ONE entry, patched in place. Pushing a new entry per navigation would pile
  // tags up for the lifetime of the session. It is created LAZILY on the first
  // MATCH, so a session that never matches never touches the head at all (R2).
  let entry: SeoHeadEntryLike | null = null

  // R5 — armed until the initial navigation is seen (or until the first
  // navigation proves it was installed after `isReady()`). Bounds the guard to
  // AT MOST ONE skipped write for the lifetime of the session, so a route that
  // somehow looks start-like later can never silently lose its head.
  let initialNavigationPending = true

  // ── S6 / S4 — the baked head, captured NOW ────────────────────────────────
  // Taken at INSTALL time on purpose: this runs at app entry, after
  // `createHead()` but before `app.mount()`, and Unhead's DOM write is
  // debounced to a later task — so `document.head` at this instant holds the
  // tags the BUILD baked and nothing else. Snapshotting later would capture
  // Unhead's own output and let the sweep delete live runtime tags.
  // `<title>` and `ld+json` are never collected (S3 / S1).
  let bakedManagedTags: Element[] = snapshotBakedManagedHeadTags()
  let bakedSweepPending = true

  return router.afterEach((to, from, failure) => {
    const path = to && typeof to.path === 'string' ? to.path : ''
    const normalizedPath = normalizeSeoHeadPath(path)

    // ── R4 — a FAILED navigation is not a navigation ─────────────────────────
    // vue-router fires `afterEach` for aborted / cancelled / duplicated
    // navigations too, and on failure the visitor is STILL ON `from`. Writing
    // `to`'s head here is the exact divergence this module exists to close,
    // just pointed the other way: the guard rejects `/pricing -> /account` and
    // the visitor reads pricing under the account head, `noindex` and all.
    // Checked BEFORE the manifest branches so it holds even when inert.
    if (failure) {
      onResolve?.({
        path,
        normalizedPath,
        matched: null,
        reason: 'navigation-failed',
        title: null,
        sweptBakedTags: 0,
      })
      return
    }

    // ── R5 — the initial document keeps its baked head ───────────────────────
    if (initialNavigationPending) {
      const initial = isStartLocation(from)
      initialNavigationPending = false
      if (initial) {
        onResolve?.({
          path,
          normalizedPath,
          matched: null,
          reason: 'initial-navigation',
          title: null,
          sweptBakedTags: 0,
        })
        return
      }
    }

    // No manifest reached the runtime (plugin absent, or an empty pages.yaml).
    // INERT, NOT FALLBACK: without the manifest there is no trustworthy key, and
    // the untrustworthy-key case is precisely R2.
    if (!routeMap) {
      onResolve?.({
        path,
        normalizedPath,
        matched: null,
        reason: 'no-manifest',
        title: null,
        sweptBakedTags: 0,
      })
      return
    }

    const matched = routeMap.get(normalizedPath)

    // ── R2 — the whole point of this branch is that it writes NOTHING ────────
    // No default title. No clear. No empty patch. `return` is the behaviour.
    if (!matched) {
      // NOTE the sweep latch is NOT spent here. A site whose visitor's first
      // click is a 404 (boogie-babies' catch-all is the normal case, R2) must
      // still get its sweep on the first REAL match, not lose it to a no-match.
      onResolve?.({
        path,
        normalizedPath,
        matched: null,
        reason: 'no-match',
        title: null,
        sweptBakedTags: 0,
      })
      return
    }

    // ── R1 — the slug comes from the MANIFEST, never from `to.meta` ──────────
    const forceNoindex = noindexSet.has(matched.path) || (!!matched.slug && noindexSet.has(matched.slug))

    // Resolve through the SHIPPED emitter, with the manifest path (not the
    // router path) so the canonical is byte-identical to the baked one — KEPT
    // bakes `/blog/`, its router says `/blog`, and the canonical must not move.
    const tags = buildHeadTags(matched.slug, matched.path, seo, {
      // MUST MATCH `dcsSeoPlugin`'s emitter call. `includeKeywords` defaults to
      // false to keep the legacy `useSEO` runtime byte-identical to its history;
      // the emitter opts in, so a re-assert that did not would leave the
      // PREVIOUS post's baked `<meta name="keywords">` in the DOM while title,
      // description and canonical all moved (C-361 F4, verified at
      // dcsSeoPlugin.ts). This is a new API — the old composable's omission is
      // not a compatibility constraint on it.
      includeKeywords: true,
      fallbackTitle: matched.title,
      robots: forceNoindex ? 'noindex, nofollow' : undefined,
    })

    const input: SeoHeadInput = { title: tags.title, meta: tags.meta, link: tags.link }

    // ── S6 — SWEEP ONCE, on the FIRST MATCH (DEFECT F-A, Q-453 = A) ──────────
    // Unhead removes only what it owns, and it never owned the baked tags. So
    // remove the baked managed tags THIS input does not re-declare, handing the
    // managed head to the runtime for the rest of the session.
    //
    // BEFORE the push, so Unhead's reconciliation pass never sees the strays —
    // and the excluded set (S2) is exactly the identities in `input`, so this
    // can only ever touch elements Unhead has no entry for and therefore cannot
    // have adopted. Once is SUFFICIENT, not a budget: afterwards every managed
    // element is either runtime-owned (Unhead drops it correctly) or gone.
    let sweptBakedTags = 0
    if (bakedSweepPending) {
      bakedSweepPending = false
      sweptBakedTags = sweepBakedManagedHeadTags(bakedManagedTags, input)
      // Spent — drop the DOM references rather than hold them for the session.
      bakedManagedTags = []
    }

    if (entry) entry.patch(input)
    else entry = head.push(input)

    onResolve?.({
      path,
      normalizedPath,
      matched,
      reason: 'applied',
      title: tags.title,
      sweptBakedTags,
    })
  })
}
